import React from "react"
import type { DunningCaseFacts, DunningPaymentPromiseOutcome } from "../types"
import {
  formatDateTime,
  formatYen,
  isPromiseActiveNow,
  overdueDaysClass,
  Panel,
  PROMISE_OUTCOME_CONFIG,
} from "./shared"

const REFRESH_STATUS_LABELS: Record<DunningCaseFacts["refreshStatus"], string> = {
  ok: "最新",
  failed: "更新失敗",
  stale: "古い",
}

const REFRESH_STATUS_CLASSES: Record<DunningCaseFacts["refreshStatus"], string> = {
  ok: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
  stale: "bg-amber-100 text-amber-800",
}

/**
 * 契約ごとの遅延内訳テーブル（契約2件以上のときのみ表示）。
 * 合計行は出さない: 白紙契約を除いた内訳の合計はサマリー（BE の amounts も
 * 白紙契約を除外して集計する）と一致し、情報が重複するため。
 */
const ContractDelinquencyBreakdown: React.FC<{
  contracts: DunningCaseFacts["contracts"]
}> = ({ contracts }) => {
  return (
    <div className="mt-5 overflow-x-auto">
      <h4 className="text-[1.2rem] font-semibold text-black">契約ごとの内訳</h4>
      <table className="mt-2 w-full text-[1.25rem] tabular-nums">
        <thead>
          <tr className="border-b border-gray-200 text-[1.1rem] text-black">
            <th className="py-1.5 pr-3 text-left font-medium">契約ID</th>
            <th className="py-1.5 pr-3 text-right font-medium">遅延金額</th>
            <th className="py-1.5 pr-3 text-right font-medium">遅延損害金</th>
            <th className="py-1.5 text-right font-medium">遅延総額</th>
          </tr>
        </thead>
        <tbody>
          {contracts.map((contract) => {
            const hasContractAmount =
              contract.principalDue != null || contract.lateChargeDue != null
            return (
              <tr key={contract.contractId} className="border-b border-dashed border-gray-200">
                <td className="py-1.5 pr-3 text-black">{contract.contractId}</td>
                <td className="py-1.5 pr-3 text-right text-black">
                  {formatYen(contract.principalDue)}
                </td>
                <td className="py-1.5 pr-3 text-right text-black">
                  {formatYen(contract.lateChargeDue)}
                </td>
                <td className="py-1.5 text-right font-medium text-black">
                  {hasContractAmount
                    ? formatYen((contract.principalDue ?? 0) + (contract.lateChargeDue ?? 0))
                    : "—"}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** 最新約束の後ろに API の結果をそのまま出す outcome（約束中は出さない） */
const LATEST_PROMISE_RESULT_OUTCOMES: DunningPaymentPromiseOutcome[] = [
  "fulfilled",
  "broken",
  "unknown",
]

/**
 * 最新約束（promise.latestScheduledOn）の結果。判定は API（dunningPromises の outcome）で、ここでは選ぶだけ。
 * 最新約束は集計時点の有効な督促画面の約束と旧来の振込予定（取り込み元・写しは除く）の期日の最大なので、
 * 期日の時刻が一致する有効な督促画面の約束を使う。日末の期日の秒は登録元で違う（担当者の登録は 23:59:00、
 * 督促LIFF 由来と旧来の振込予定は 23:59:59）ので、旧来の振込予定を同じ日の担当者の約束と取り違えないよう、
 * 分単位でなく時刻そのもので比べる。一致する約束が無いか結果が割れるときは null。
 */
const latestPromiseOutcome = (
  facts: Pick<DunningCaseFacts, "promise" | "dunningPromises">
): DunningPaymentPromiseOutcome | null => {
  const latestAt = facts.promise.latestScheduledOn
    ? Date.parse(facts.promise.latestScheduledOn)
    : NaN
  if (Number.isNaN(latestAt)) return null
  const outcomes = new Set(
    (facts.dunningPromises ?? [])
      .filter(
        (promise) =>
          promise.status === "active" &&
          promise.outcome != null &&
          Date.parse(promise.scheduledAt) === latestAt
      )
      .map((promise) => promise.outcome)
  )
  if (outcomes.size !== 1) return null
  const [outcome] = Array.from(outcomes)
  return outcome ?? null
}

/**
 * 最新約束の後ろの文言と、赤で出すか。履行・不履行・判定不可は API の結果をそのまま出し、不履行だけ赤。
 * 結果が分からない（対応する約束が無い）か約束中のときは、これまでどおり「不履行の可能性」を赤で出す
 */
const latestPromiseResult = (
  outcome: DunningPaymentPromiseOutcome | null
): { label: string; alert: boolean } =>
  outcome && LATEST_PROMISE_RESULT_OUTCOMES.includes(outcome)
    ? { label: PROMISE_OUTCOME_CONFIG[outcome].label, alert: outcome === "broken" }
    : { label: "不履行の可能性", alert: true }

/** 延滞サマリー（スナップショット時点の金額・約束）。AIドラフト検証のケース表示でも読み取り専用で使う */
export const DelinquencySummaryPanel: React.FC<{ facts: DunningCaseFacts }> = ({ facts }) => {
  const { amounts, promise } = facts
  const totalDue = amounts.totalDue ?? null
  // 遅延金額 = 遅延元本＋遅延手数料（遅延損害金を含まない）。一覧の「契約毎約定遅延額」と同じデータ源
  const overdueAmount = amounts.principalDue ?? null
  // 白紙契約（active: false）は一覧・契約数バッジと同様に内訳へ出さない
  const activeContracts = (facts.contracts ?? []).filter((contract) => contract.active !== false)
  const latestResult = latestPromiseResult(latestPromiseOutcome(facts))

  return (
    <Panel
      title="遅延状況"
      headerRight={
        <div className="flex items-center gap-2 text-[1.1rem] text-black">
          <span
            className={`rounded-full px-2.5 py-0.5 font-medium ${REFRESH_STATUS_CLASSES[facts.refreshStatus]}`}
          >
            {REFRESH_STATUS_LABELS[facts.refreshStatus]}
          </span>
          <span>更新: {formatDateTime(facts.asOf)}</span>
        </div>
      }
    >
      <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          [
            "遅延日数",
            <span
              key="overdue"
              className={`font-bold tabular-nums ${overdueDaysClass(amounts.overdueDays)}`}
            >
              {amounts.overdueDays != null ? `${amounts.overdueDays}日` : "—"}
            </span>,
          ],
          ["遅延金額", formatYen(overdueAmount)],
          ["遅延損害金", formatYen(amounts.lateChargeDue)],
          ["遅延総額", formatYen(totalDue)],
        ].map(([label, value]) => (
          <div key={String(label)}>
            <dt className="text-[1.1rem] font-medium text-black">{label}</dt>
            <dd className="mt-1 text-[1.4rem] font-medium tabular-nums text-black">{value}</dd>
          </div>
        ))}
      </dl>

      {activeContracts.length >= 2 && <ContractDelinquencyBreakdown contracts={activeContracts} />}

      <div className="mt-5">
        <div className="rounded-lg bg-gray-50 p-4">
          <h4 className="text-[1.2rem] font-semibold text-black">支払約束</h4>
          {/* promise.active は再計算時点の値で約束日時を過ぎても残るので、約束日時と現在時刻で判定する */}
          {isPromiseActiveNow(promise) ? (
            <p className="mt-2 text-[1.3rem] text-black">
              <span className="font-medium text-green-600">あり</span>
              {promise.scheduledOn && (
                <span className="ml-2 tabular-nums">
                  期日 {formatDateTime(promise.scheduledOn)}
                </span>
              )}
              {promise.amount != null && (
                <span className="ml-2 tabular-nums">{formatYen(promise.amount)}</span>
              )}
            </p>
          ) : (
            <p className="mt-2 text-[1.3rem] text-black">
              有効な約束はありません。
              {promise.latestScheduledOn && (
                <span
                  className={`ml-1 font-medium ${latestResult.alert ? "text-red-500" : "text-black"}`}
                >
                  最新約束 {formatDateTime(promise.latestScheduledOn)}（{latestResult.label}）
                </span>
              )}
            </p>
          )}
        </div>
      </div>
    </Panel>
  )
}
